import { Warning } from "@mui/icons-material";
import { Box, Collapse, FormControlLabel, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import APIDocsLink from "../../../../../components/common/APIDocsLink/APIDocsLink.tsx";
import BriaCheckbox from "../../../../../components/common/BriaCheckbox/BriaCheckbox";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput";
import BriaTooltip from "../../../../../components/common/BriaTooltip/BriaTooltip.tsx";
import SurpriseButton from "../../../../../components/common/SurpriseButton/SurpriseButton";
import { APPS } from "../../../../../constants/AppsConstants.ts";
import LinkConstants from "../../../../../constants/LinkConstants.ts";
import { isDnaiOrg } from "../../../../../constants/OrgConstants.ts";
import { useAppStore } from "../../../../../hooks/useStores";
import { isTextToVector } from "../../../../../utils/index.ts";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";
import TextToImageGenerateButton from "./Button/TextToImageGenerateButton.tsx";
import PromptTextToImageError from "./Error/PromptTextToImageError/PromptTextToImageError.tsx";
import styles from "./TextToImageSearch.module.scss";

const TextToImageSearch = () => {
	const { t, i18n } = useTranslation("translation", { keyPrefix: "playground.textToImage.generator.searchBox" });
	const { textToImageStore, playgroundStore } = useAppStore();
	const isFiboModel =
		textToImageStore.config.model === "fibo" ||
		(!!textToImageStore.config.tailored_model_id && textToImageStore.config.training_version === "fibo");
	const isNewTg = playgroundStore.selectedConfig == APPS.TG_IMAGE_GENERATION;

	useEffect(() => {
		if (textToImageStore.config.tailored_model_id && textToImageStore.config?.training_version !== "fibo") {
			textToImageStore.handleConfigChange("prompt_enhancement", false);
		} else if (!isTextToVector()) {
			textToImageStore.handleConfigChange("prompt_enhancement", true);
		}

		return () => {
			textToImageStore.errors = {};
		};
	}, [textToImageStore.config.tailored_model_id]);

	return (
		<Box className={playgroundStore.isText2ImagePopupOpened ? styles.rootModal : styles.root}>
			{/* TODO: Combine the warning into TextToImageError for generate errors - Combined for NewTg*/}
			{isNewTg ? null : textToImageStore.IpSignalWarning ? (
				!iframeStore.isIframe() && (
					<Collapse
						in={textToImageStore.IpSignalWarning}
						onExited={() => (textToImageStore.errors.generate = undefined)}
					>
						<Box className={styles.warningMessageContainer}>
							<Warning />
							<Typography className={styles.warningMessage}>
								{i18n.t(`playground.textToImage.errors.ipSignalWarning`)}
							</Typography>
						</Box>
					</Collapse>
				)
			) : (
				<PromptTextToImageError />
			)}
			<Box className={styles.searchBoxWrapper}>
				<BriaInput
					value={textToImageStore.config.prompt}
					onChange={(e) => textToImageStore.handleConfigChange("prompt", e.target.value)}
					onEnterPress={textToImageStore.generateTextToImage}
					className={styles.searchBox}
					placeholder={isDnaiOrg() ? t("placeholderDnai") : t("placeholder")}
					InputProps={{
						endAdornment: !isDnaiOrg() ? (
							<SurpriseButton
								textsListType={
									isTextToVector()
										? "textToVector"
										: textToImageStore.config.model_version === "3.2"
											? "textToImage32"
											: "textToImage2"
								}
								onSurprise={(_e, prompt) => textToImageStore.handleConfigChange("prompt", prompt)}
							/>
						) : undefined,
					}}
					inputProps={{ className: styles.inputBase }}
					rootClass={styles.inputContainer}
					maxRows={4}
					onMouseEnter={() => (playgroundStore.disableSelecto = true)}
					onMouseLeave={() => (playgroundStore.disableSelecto = false)}
					multiline
				/>

				<TextToImageGenerateButton />
			</Box>
			{!isTextToVector() && (
				<>
					{isFiboModel ? (
						<BriaTooltip className={styles.tooltip} placement="top" title={t("fiboTooltip")}>
							<FormControlLabel
								disabled={textToImageStore.isImageReferenceEnabled() || isFiboModel}
								control={
									<BriaCheckbox
										checked={
											textToImageStore.config?.prompt_enhancement &&
											!textToImageStore.isImageReferenceEnabled()
										}
										onChange={(e) =>
											textToImageStore.handleConfigChange("prompt_enhancement", e.target.checked)
										}
									/>
								}
								label={isDnaiOrg() ? t("enhancedPromptDnai") : t("enhancePrompt")}
								className={styles.controlStyle}
							/>
						</BriaTooltip>
					) : (
						<FormControlLabel
							disabled={
								textToImageStore.isImageReferenceEnabled() || textToImageStore.config.model === "fibo"
							}
							control={
								<BriaCheckbox
									checked={
										textToImageStore.config?.prompt_enhancement &&
										!textToImageStore.isImageReferenceEnabled()
									}
									onChange={(e) =>
										textToImageStore.handleConfigChange("prompt_enhancement", e.target.checked)
									}
								/>
							}
							label={isDnaiOrg() ? t("enhancedPromptDnai") : t("enhancePrompt")}
							className={styles.controlStyle}
						/>
					)}

					<APIDocsLink link={LinkConstants.BRIA_API_PROMPT_ENHANCE} />
				</>
			)}
		</Box>
	);
};

export default observer(TextToImageSearch);
